/**
 * RatingButtons — like/dislike thumbs for any assistant output.
 *
 * Drop this next to a generated image or a copy message. The component
 * owns:
 *   - current rating state (hydrated lazily from the parent's map of
 *     "my ratings", to avoid one network call per card)
 *   - the feedback modal that opens on dislike so users can explain why
 *
 * Usage:
 *   const { ratingFor, setRating, clearRating } = useMyRatings();
 *   ...
 *   <RatingButtons
 *       itemType="image"   // "image" | "copy"
 *       itemId={img.library_id}
 *       chatId={chatId}
 *       chatTitle={chatTitle}
 *       itemPreview={img.storage_path}
 *       initial={ratingFor("image", img.library_id)}
 *       onChange={(value, feedback) => setRating("image", img.library_id, value, feedback)}
 *   />
 */
import { useEffect, useState } from "react";
import { ThumbsUp, ThumbsDown, X } from "lucide-react";
import { toast } from "sonner";
import { api } from "../lib/api";

export default function RatingButtons({
    itemType,
    itemId,
    chatId,
    chatTitle,
    itemPreview,
    initial = null, // { rating: "like"|"dislike", feedback?: string } | null
    onChange,
    size = "sm",
    className = "",
}) {
    const [current, setCurrent] = useState(initial?.rating || null);
    const [feedback, setFeedback] = useState(initial?.feedback || "");
    const [showFeedback, setShowFeedback] = useState(false);
    const [busy, setBusy] = useState(false);

    useEffect(() => {
        // Re-hydrate when parent's initial changes (e.g. after the
        // "my ratings" call resolves after first render).
        if (initial?.rating !== current) setCurrent(initial?.rating || null);
        if ((initial?.feedback || "") !== feedback) setFeedback(initial?.feedback || "");
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [initial?.rating, initial?.feedback]);

    const persist = async (value, fb = null) => {
        setBusy(true);
        try {
            await api.post("/ratings", {
                item_type: itemType,
                item_id: itemId,
                rating: value,
                feedback: fb,
                chat_id: chatId || null,
                chat_title: chatTitle || null,
                item_preview: itemPreview || null,
            });
            onChange?.(value, fb);
        } catch (e) {
            toast.error("No se pudo guardar la valoración");
            // rollback optimistic UI
            setCurrent(initial?.rating || null);
        } finally {
            setBusy(false);
        }
    };

    const clear = async () => {
        setBusy(true);
        try {
            await api.delete(`/ratings/${itemType}/${itemId}`);
            setCurrent(null);
            setFeedback("");
            onChange?.(null, null);
        } catch {
            toast.error("No se pudo quitar la valoración");
        } finally {
            setBusy(false);
        }
    };

    const onLike = () => {
        if (current === "like") {
            clear();
            return;
        }
        setCurrent("like");
        persist("like", null);
    };

    const onDislike = () => {
        if (current === "dislike") {
            clear();
            return;
        }
        setCurrent("dislike");
        // Optimistically persist the dislike with no feedback so it's
        // saved even if the user closes the feedback modal without
        // typing. They can still add text afterwards.
        persist("dislike", feedback || null);
        setShowFeedback(true);
    };

    const submitFeedback = async (txt) => {
        // `txt` comes straight from the modal callback so we don't have
        // to wait for setFeedback's async update to land in state.
        const cleaned = (txt ?? feedback).trim();
        await persist("dislike", cleaned || null);
        setShowFeedback(false);
        if (cleaned) toast.success("Comentario enviado");
    };

    const btnBase =
        size === "xs"
            ? "w-6 h-6 text-xs"
            : "w-8 h-8";

    return (
        <>
            <div
                data-testid={`rating-${itemType}-${itemId}`}
                className={`inline-flex items-center gap-1 ${className}`}
            >
                <button
                    type="button"
                    onClick={onLike}
                    disabled={busy}
                    data-testid={`rating-like-${itemType}-${itemId}`}
                    title={current === "like" ? "Quitar me gusta" : "Me gusta"}
                    className={`${btnBase} rounded-full border hairline flex items-center justify-center transition-colors ${
                        current === "like"
                            ? "border-emerald-500 text-emerald-400 bg-emerald-500/10"
                            : "border-ink-600 text-fog-400 hover:text-emerald-400 hover:border-emerald-500/50"
                    } disabled:opacity-40`}
                >
                    <ThumbsUp className="w-3.5 h-3.5" />
                </button>
                <button
                    type="button"
                    onClick={onDislike}
                    disabled={busy}
                    data-testid={`rating-dislike-${itemType}-${itemId}`}
                    title={current === "dislike" ? "Quitar dislike" : "No me gusta"}
                    className={`${btnBase} rounded-full border hairline flex items-center justify-center transition-colors ${
                        current === "dislike"
                            ? "border-rose-500 text-rose-400 bg-rose-500/10"
                            : "border-ink-600 text-fog-400 hover:text-rose-400 hover:border-rose-500/50"
                    } disabled:opacity-40`}
                >
                    <ThumbsDown className="w-3.5 h-3.5" />
                </button>
            </div>
            {showFeedback && (
                <FeedbackModal
                    initial={feedback}
                    onClose={() => setShowFeedback(false)}
                    onSubmit={(txt) => {
                        setFeedback(txt);
                        submitFeedback(txt);
                    }}
                />
            )}
        </>
    );
}

function FeedbackModal({ initial = "", onClose, onSubmit }) {
    const [text, setText] = useState(initial);
    useEffect(() => {
        const onKey = (e) => {
            if (e.key === "Escape") onClose?.();
        };
        document.addEventListener("keydown", onKey);
        return () => document.removeEventListener("keydown", onKey);
    }, [onClose]);
    return (
        <div
            data-testid="rating-feedback-modal"
            className="fixed inset-0 z-[70] flex items-center justify-center bg-ink-950/80 backdrop-blur-sm p-4"
            onMouseDown={(e) => {
                if (e.target === e.currentTarget) onClose?.();
            }}
        >
            <div className="w-full max-w-md rounded-2xl border hairline border-ink-600 bg-ink-900 p-6">
                <div className="flex items-start justify-between gap-4 mb-4">
                    <div>
                        <p className="font-mono text-[10px] tracking-[0.25em] text-rose-400 uppercase">
                            Feedback
                        </p>
                        <h3 className="font-display text-lg text-fog-100 mt-1">
                            ¿Qué salió mal?
                        </h3>
                        <p className="text-xs text-fog-400 mt-1">
                            Cuéntanos por qué este resultado no fue útil. Tu
                            comentario ayuda a mejorar las próximas generaciones.
                        </p>
                    </div>
                    <button
                        type="button"
                        onClick={onClose}
                        className="w-8 h-8 rounded-lg text-fog-400 hover:text-fog-100"
                        aria-label="Cerrar"
                    >
                        <X className="w-4 h-4" />
                    </button>
                </div>
                <textarea
                    data-testid="rating-feedback-text"
                    value={text}
                    onChange={(e) => setText(e.target.value)}
                    rows={4}
                    autoFocus
                    placeholder="Por ejemplo: el tono no coincide con la marca, la composición está desbalanceada…"
                    className="w-full rounded-lg bg-ink-950 border hairline border-ink-600 text-fog-100 px-3 py-2 text-sm outline-none focus:border-periwinkle-500 placeholder:text-fog-500"
                />
                <div className="flex items-center justify-end gap-2 mt-4">
                    <button
                        type="button"
                        onClick={onClose}
                        data-testid="rating-feedback-skip"
                        className="px-3 py-1.5 rounded-lg text-xs text-fog-300 hover:text-fog-100"
                    >
                        Omitir
                    </button>
                    <button
                        type="button"
                        onClick={() => onSubmit(text)}
                        data-testid="rating-feedback-submit"
                        className="px-4 py-1.5 rounded-lg bg-periwinkle-500 hover:bg-periwinkle-600 text-ink-950 text-xs font-medium"
                    >
                        Enviar
                    </button>
                </div>
            </div>
        </div>
    );
}
